<template>
  <div class="group-card" :style="{ background: isGroupPm ? 'rgb(var(--v-theme-surface-tertiary))' : 'rgb(var(--v-theme-surface-primary))' }">
    <v-row no-gutters class="align-start">
      <!-- NAME OF GROUP -->
      <v-col>
        <template v-if="editGroupName">
          <GroupNameEdit
              v-if="editGroupName"
              :group="group"
              @cancel="editGroupName = false"
          />
        </template>
        <template v-else-if="!isGroupPm && isCurrentUserPm">
          <div class="d-flex align-center group-name text-text-primary" @click="editGroupName = true">
            <h3 class="text-truncate">{{ `${group.name}` }}</h3>
            <h3>&nbsp;{{ `(${group.members.length})` }}</h3>
          </div>
        </template>
        <template v-else>
          <h3 class="group-name-without-hover text-text-primary">{{ `${group.name} (${group.members.length})` }}</h3>
        </template>
      </v-col>
      <v-col v-if="!isGroupPm && isCurrentUserPm" cols="auto">
        <div class="d-flex mt-n2">
          <AppTooltip>
            <template #activator="{ props }">
              <app-button
                v-bind="props"
                type="icon"
                @click="editGroupName = true"
              >
                <app-material-icon name="edit" />
              </app-button>
            </template>
            <span>{{ $t('project.members.Group.tooltipRename') }}</span>
          </AppTooltip>
          <DeleteGroup :group-to-delete="group"/>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="hideMembers"
           no-gutters
           class="mt-0"
    >
      <v-col>
        <AppTooltip>
          <template #activator="{ props }">
            <v-chip
                class="hide-members-chip"
                label
                size="small"
                color="error"
                v-bind="props"
            >
              <span class="text--bold text--x-small text-uppercase">{{ $t('project.members.Group.confidentialGroup') }}</span>
              <app-material-icon name="lock" :size="16" class="ml-1" />
            </v-chip>
          </template>
          <span v-html="$t('project.members.Group.hiddenParticipantsInfo')"/>
        </AppTooltip>
      </v-col>
    </v-row>

    <draggable
        :component-data="{
                 tag: 'v-row',
                 noGutters: true,
                 type: 'transition-group',
                 style: {
                   marginTop: '12px',
                   marginBottom: '12px',
                   maxHeight: '350px',
                   overflowY: 'auto',
                 },
               }"
        :list="group.members"
        item-key="id"
        v-bind="dragOptions"
        @change="moveUserGroup(group.id, $event)"
    >
      <template #item="{ element: member }">
        <Member
            :group="group"
            :member="member"
            :mnemo="mnemo"
            @update:member="updateMember"
        />
      </template>
    </draggable>

    <v-row no-gutters>
      <v-col>
        <app-button
          type="outlined"
          variant="neutral"
          full-width
          @click="seeAllMembersDialogIsOpen = true"
        >
          {{ $t('project.members.Group.seeAllMembers') }}
        </app-button>
      </v-col>
    </v-row>
    <v-row v-if="!isGroupPm && isCurrentUserPm" no-gutters style="margin-top: 10px;">
      <v-col>
        <app-toggle
          :model-value="hideMembers"
          :loading="patchGroupLoading"
          :label="$t('project.members.Group.hideGroupMembers')"
          hide-details
          @update:model-value="hideMembersHandler"
        />
      </v-col>
    </v-row>
    <v-row v-if="isCurrentUserPm" no-gutters class="mt-3">
      <v-col>
        <app-button
            type="outlined"
            variant="neutral"
            full-width
            data-locator="add-participant-button"
            is-icon-color-custom
            @click="addMemberDialogIsOpen = true"
        >
          <template #left-icon>
            <app-material-icon name="add_circle" size="xs" />
          </template>
          {{ $t('project.members.Group.addMember') }}
        </app-button>
      </v-col>
    </v-row>
  </div>

  <app-modal
      v-model="confirmDialog"
      size="md"
      @ok="validateMembersDisplay"
      @cancel="confirmDialog = !confirmDialog"
  >
    <template #title>{{ $t('project.members.Group.confirmDialog.title') }}</template>
    <template #body>{{ $t('project.members.Group.confirmDialog.body') }}</template>
  </app-modal>

  <GroupAddMemberDialog
      v-if="addMemberDialogIsOpen"
      :group="group"
      @close="addMemberDialogIsOpen = false"
  />

  <MembersSeeAllDialog
    v-if="seeAllMembersDialogIsOpen"
    :group="group"
    @close="seeAllMembersDialogIsOpen = false"
  />
</template>

<script>
import draggable from 'vuedraggable'
import { mapState, mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { toast } from '@/common/utils/toast'

import GroupAddMemberDialog from './dialogs/GroupAddMemberDialog.vue'
import DeleteGroup from './dialogs/GroupDeleteDialog.vue'
import MembersSeeAllDialog from './dialogs/MembersSeeAllDialog.vue'
import GroupNameEdit from './GroupNameEdit.vue'
import Member from './Member.vue'

export default {
  name: 'Group',
  components: {
    AppTooltip,
    DeleteGroup,
    GroupAddMemberDialog,
    GroupNameEdit,
    Member,
    MembersSeeAllDialog,
    draggable,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      addMemberDialogIsOpen: false,
      confirmDialog: false,
      dragging: false,
      dragUser: {
        toGroup: null,
        userId: null,
      },
      editGroupName: false,
      editingName: false,
      hideMembers: false,
      patchGroupLoading: false,
      seeAllMembersDialogIsOpen: false,
    }
  },
  computed: {
    ...mapState('groups', ['groups']),
    ...mapGetters('room', ['isCurrentUserPm']),
    isGroupPm () {
      return this.group.isPm
    },
    users: {
      get: function () {
        return this.$store.state.users
      },
      set: function (newValue) {
        this.$store.commit('setUsers', newValue)
      },
    },
    dragOptions () {
      return {
        animation: 150,
        delay: 500,
        delayOnTouchOnly: true,
        disabled: !this.isCurrentUserPm,
        emptyInsertThreshold: 100,
        filter: '.ignore-draggable-elements',
        forceFallback: true,
        group: "members",
        handle: '.Member',
      }
    },
  },
  created () {
    this.hideMembers = this.group.hideMembers
  },
  methods: {
    ...mapActions('groups', ['PATCH_GROUP', 'PATCH_USER_ACCESS', 'GET_GROUPS']),
    deleteGroup (group) {
      this.$store.commit('setDialogDelete', {
        active: true,
        action: () => {
          this.$store.commit(
            'setUsers',
            this.users.filter((item) => item.id !== group.id),
          )
        },
      })
    },
    editName () {
      toast.success(this.$t('project.members.Group.savedModification'))
    },
    async hideMembersHandler () {
      if (this.hideMembers) {
        this.confirmDialog = true
      } else {
        await this.hideGroupMembers()
      }
    },
    async validateMembersDisplay () {
      this.confirmDialog = false
      await this.showGroupMembers()
    },
    async hideGroupMembers () {
      const successCallback = () => toast.success(this.$t('project.members.Group.messages.hideMembers'))
      await this.setGroupMembersVisibility({ membersAreHidden: true }, successCallback)
    },
    async showGroupMembers () {
      const successCallback = () => toast.success(this.$t('project.members.Group.messages.notHideMembers'))
      await this.setGroupMembersVisibility({ membersAreHidden: false }, successCallback)
    },
    async setGroupMembersVisibility ({ membersAreHidden }, successCallback) {
      this.patchGroupLoading = true

      try {
        const updatedGroup = await this.PATCH_GROUP({
          groupId: this.group.id,
          data: {
            hideMembers: membersAreHidden,
          },
        })
        this.hideMembers = updatedGroup.hideMembers
        successCallback()
      } finally {
        this.patchGroupLoading = false
      }
    },
    async moveUserGroup (groupId, $event) {
      if (Object.hasOwn($event, 'added')) {
        this.dragUser.toGroup = groupId
        if (!this.dragUser.userId) {
          this.dragUser.userId = $event.added.element.id
        }
      }
      if (this.dragUser.toGroup && this.dragUser.userId) {
        try {
          await this.PATCH_USER_ACCESS({
            memberId: this.dragUser.userId,
            data: {
              newGroupId: this.dragUser.toGroup,
            },
          })

          toast.success(this.$t('project.members.Group.messages.successChangeGroup'))
          this.dragUser.toGroup = null
          this.dragUser.userId = null
        } catch {
          toast.error(this.$t('project.members.Group.messages.errorChangeGroup'))
        } finally {
          await this.GET_GROUPS(this.mnemo)
        }
      }
    },
    updateMember (updatedMember) {
      const originalGroup = this.groups.find(group => group.id === this.group.id)
      const index = originalGroup.members.findIndex(member => member.id === updatedMember.id)
      if (index !== -1) {
        originalGroup.members.splice(index, 1, updatedMember)
      }
    },
  },
}
</script>

<style lang="scss" scoped >
.group-card {
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgb(var(--v-theme-border-primary));
}
.group-name {
  cursor: pointer !important;
  font-weight: 600 !important;
  border: 1px dashed transparent;
  border-radius: 4px;
  padding-left: 6px;
  padding-right: 6px;
  min-height: 24px;
  min-width: 100px;
  &:hover {
    border-color: #bdbddb;
    background-color: #eee;
  }
}
.group-name-without-hover{
  font-weight: 600 !important;
}

.hide-member-toggle {
  line-height: 1.2;
}

.hide-members-chip {
  color: rgb(var(--v-theme-text-danger));
  background-color: rgb(var(--v-theme-surface-danger-subtle));
}

/* transition list */
.list-groups-item {
  transition: all 0.5s;
}
.list-groups-enter,
.list-groups-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}
.list-groups-leave-active {
  position: absolute;
  z-index: -1;
}

/* transition list */
.list-members-item {
  transition: all 0.5s;
}
.list-members-enter,
.list-members-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
.list-members-leave-active {
  position: absolute;
  z-index: -1;
}
</style>
